// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Supabase migration V2 - PREVIEW ONLY)
// protected: NO  -> Halaman Batalkan Produksi V2 (SLICE T-B2).
// =============================================================================
// Batalkan Produksi (dokumen alur wewenang):
// - Admin Produksi: pilih 1 Work Order -> pilih unit ber-status PRODUCED -> alasan WAJIB
//   -> ajukan. "X dari Y unit akan dibatalkan" tampil jelas sebelum diajukan.
// - Kepala Cabang region sama: setujui / tolak. Konfirmasi persetujuan MENYEBUT ANGKA +
//   peringatan PERMANEN (QR mati selamanya, dokumen tak bisa dibatalkan).
// - Owner: read-only (bertindak via Assist Mode). Region ditegakkan backend.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PackageX, Loader2, Send, Check, Ban, RefreshCw, FileText, AlertTriangle, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { useV2Auth } from "@/v2/V2AuthContext";
import { openPdf } from "@/v2/components/helpers";
import { EmptyState, ListSkeleton } from "@/v2/components/ux";

const PRODUKSI = ["ADMIN_PRODUKSI_SBY", "ADMIN_PRODUKSI_SMD"];
const KACAB = ["KACAB_SBY", "KACAB_SMD"];

const STATUS_LABEL = { DIAJUKAN: "Menunggu Persetujuan", DISETUJUI: "Disetujui", DITOLAK: "Ditolak" };
const STATUS_VARIANT = { DIAJUKAN: "secondary", DISETUJUI: "default", DITOLAK: "destructive" };
const fmtTs = (s) => { if (!s) return "-"; try { return new Date(s).toLocaleString("id-ID", { dateStyle: "short", timeStyle: "short" }); } catch { return s; } };

export default function BatalkanProduksiPage() {
  const { me, api } = useV2Auth();
  const role = me?.role;
  const canSubmit = PRODUKSI.includes(role);
  const canDecide = KACAB.includes(role);

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);

  // Form pengajuan (Admin Produksi)
  const [wos, setWos] = useState([]);
  const [woId, setWoId] = useState("");
  const [units, setUnits] = useState([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [checked, setChecked] = useState({});
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Dialog keputusan (Kacab)
  const [decision, setDecision] = useState(null); // { doc, mode: 'approve'|'reject' }
  const [decisionReason, setDecisionReason] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadDocs = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api("/api/v2/production-cancel");
      const d = await r.json();
      setDocs(Array.isArray(d) ? d : []);
    } catch { toast.error("Gagal memuat daftar dokumen"); }
    setLoading(false);
  }, [api]);

  const loadWos = useCallback(async () => {
    if (!canSubmit) return;
    try {
      const r = await api("/api/v2/production-cancel/work-orders");
      const d = await r.json();
      setWos(Array.isArray(d) ? d : []);
    } catch { /* noop */ }
  }, [api, canSubmit]);

  useEffect(() => { loadDocs(); loadWos(); }, [loadDocs, loadWos]);

  const loadUnits = useCallback(async (id) => {
    setUnits([]); setChecked({});
    if (!id) return;
    setUnitsLoading(true);
    try {
      const r = await api(`/api/v2/production-cancel/work-orders/${id}/produced-units`);
      const d = await r.json();
      const u = d?.units || [];
      setUnits(u);
      const all = {}; u.forEach((x) => { all[x.qr_id] = true; });
      setChecked(all); // default: pilih semua
    } catch { toast.error("Gagal memuat unit"); }
    setUnitsLoading(false);
  }, [api]);

  const onSelectWo = (id) => { setWoId(id); loadUnits(id); };

  const selectedQrs = useMemo(() => units.filter((u) => checked[u.qr_id]).map((u) => u.qr_id), [units, checked]);
  const allChecked = units.length > 0 && selectedQrs.length === units.length;
  const toggleAll = () => {
    if (allChecked) setChecked({});
    else { const all = {}; units.forEach((x) => { all[x.qr_id] = true; }); setChecked(all); }
  };

  const submit = async () => {
    if (!woId) return toast.warning("Pilih Work Order dulu");
    if (selectedQrs.length === 0) return toast.warning("Pilih minimal satu unit");
    if (!reason.trim()) return toast.warning("Alasan pembatalan WAJIB diisi");
    setSubmitting(true);
    try {
      const r = await api("/api/v2/production-cancel", {
        method: "POST",
        body: JSON.stringify({ wo_id: woId, qr_ids: selectedQrs, reason: reason.trim() }),
      });
      const d = await r.json();
      if (!r.ok) { toast.error(typeof d?.detail === "string" ? d.detail : "Gagal mengajukan"); }
      else {
        toast.success(`Diajukan: ${d.doc_no} (${selectedQrs.length} unit)`);
        setWoId(""); setUnits([]); setChecked({}); setReason("");
        loadDocs(); loadWos();
      }
    } catch { toast.error("Gagal mengajukan"); }
    setSubmitting(false);
  };

  const openDecision = (doc, mode) => { setDecision({ doc, mode }); setDecisionReason(""); setAck(false); };

  const confirmDecision = async () => {
    if (!decision) return;
    const { doc, mode } = decision;
    if (mode === "approve" && !ack) return toast.warning("Centang konfirmasi bahwa Anda memahami tindakan ini permanen");
    setBusy(true);
    try {
      const r = await api(`/api/v2/production-cancel/${doc.id}/${mode}`, {
        method: "POST",
        body: JSON.stringify(mode === "reject" ? { reason: decisionReason.trim() || null } : {}),
      });
      const d = await r.json();
      if (!r.ok) { toast.error(typeof d?.detail === "string" ? d.detail : "Gagal memproses"); }
      else if (mode === "approve") {
        const res = d.result || {};
        if ((res.failed || 0) > 0) toast.warning(`Disetujui: ${res.cancelled} dibatalkan, ${res.failed} gagal`);
        else toast.success(`Disetujui: ${res.cancelled} unit dibatalkan permanen`);
        setDecision(null); loadDocs();
      } else { toast.success("Ajuan ditolak"); setDecision(null); loadDocs(); }
    } catch { toast.error("Gagal memproses"); }
    setBusy(false);
  };

  const selectedWo = wos.find((w) => w.wo_id === woId);

  return (
    <div className="space-y-6" data-testid="batalkan-produksi-page">
      <div className="flex items-center gap-3">
        <PackageX className="w-6 h-6 text-destructive" />
        <div>
          <h1 className="text-2xl font-semibold">Batalkan Produksi</h1>
          <p className="text-sm text-muted-foreground">
            Melenyapkan unit yang gagal produksi secara permanen — lewat dokumen resmi berjenjang.
          </p>
        </div>
      </div>

      {/* ============ FORM PENGAJUAN (Admin Produksi) ============ */}
      {canSubmit && (
        <Card className="p-5 space-y-4 rounded-2xl" data-testid="bp-submit-card">
          <h2 className="text-lg font-medium">Ajukan Pembatalan</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Work Order</Label>
              <Select value={woId} onValueChange={onSelectWo}>
                <SelectTrigger data-testid="bp-wo-select"><SelectValue placeholder="Pilih Work Order (ada unit produksi)" /></SelectTrigger>
                <SelectContent>
                  {wos.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">Tidak ada WO dengan unit produksi</div>}
                  {wos.map((w) => (
                    <SelectItem key={w.wo_id} value={w.wo_id} data-testid={`bp-wo-opt-${w.wo_no}`}>
                      {(w.wo_no_offline || w.wo_no)} — {w.club} ({w.produced_count} unit)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Alasan Pembatalan (wajib)</Label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                        placeholder="mis. Salah bahan / cacat produksi massal" data-testid="bp-reason-input" />
            </div>
          </div>

          {woId && (
            <div className="space-y-3">
              {unitsLoading ? <ListSkeleton rows={3} /> : units.length === 0 ? (
                <EmptyState icon={PackageX} title="Tidak ada unit produksi" description="Semua unit WO ini sudah masuk gudang atau berpindah status." />
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="bp-check-all" id="bp-all" />
                      <Label htmlFor="bp-all" className="cursor-pointer">Pilih semua</Label>
                    </div>
                    <Badge variant="secondary" data-testid="bp-selected-count">
                      {selectedQrs.length} dari {units.length} unit akan dibatalkan
                    </Badge>
                  </div>
                  <div className="max-h-72 overflow-auto rounded-lg border">
                    <Table>
                      <TableHeader><TableRow><TableHead className="w-10"></TableHead><TableHead>QR</TableHead><TableHead>Produk</TableHead><TableHead>Size</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {units.map((u) => (
                          <TableRow key={u.qr_id}>
                            <TableCell><Checkbox checked={!!checked[u.qr_id]} onCheckedChange={(v) => setChecked((p) => ({ ...p, [u.qr_id]: !!v }))} data-testid={`bp-unit-${u.qr_id}`} /></TableCell>
                            <TableCell className="font-mono text-xs">{u.qr_id}</TableCell>
                            <TableCell>{u.product_name || "-"}</TableCell>
                            <TableCell>{u.size || "-"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={submit} disabled={submitting || selectedQrs.length === 0 || !reason.trim()} data-testid="bp-submit-btn">
                      {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Ajukan Pembatalan {selectedWo ? `(${selectedQrs.length} unit)` : ""}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </Card>
      )}

      {/* ============ DAFTAR DOKUMEN ============ */}
      <Card className="p-5 space-y-4 rounded-2xl" data-testid="bp-list-card">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium">Dokumen Batalkan Produksi</h2>
          <Button variant="outline" size="sm" onClick={loadDocs} data-testid="bp-refresh"><RefreshCw className="w-4 h-4 mr-2" />Muat ulang</Button>
        </div>
        {loading ? <ListSkeleton rows={4} /> : docs.length === 0 ? (
          <EmptyState icon={PackageX} title="Belum ada dokumen" description="Ajuan pembatalan produksi akan muncul di sini." testid="bp-empty" />
        ) : (
          <div className="overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>No. Dokumen</TableHead><TableHead>Work Order</TableHead>
                  <TableHead>Status</TableHead><TableHead>Ringkasan</TableHead>
                  <TableHead>Pengaju</TableHead><TableHead>Tgl</TableHead><TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {docs.map((d) => (
                  <TableRow key={d.id} data-testid={`bp-row-${d.doc_no}`}>
                    <TableCell className="font-medium">{d.doc_no}</TableCell>
                    <TableCell>{d.wo?.wo_no_offline || d.wo?.wo_no || "-"}<div className="text-xs text-muted-foreground">{d.wo?.club}</div></TableCell>
                    <TableCell><Badge variant={STATUS_VARIANT[d.status] || "secondary"}>{STATUS_LABEL[d.status] || d.status}</Badge></TableCell>
                    <TableCell className="text-xs">
                      {d.status === "DISETUJUI"
                        ? <span>Diajukan {d.proposed_count} · Berhasil {d.cancelled_count ?? "-"} · Gagal {d.failed_count ?? "-"}</span>
                        : <span>{d.proposed_count} unit diajukan</span>}
                    </TableCell>
                    <TableCell className="text-xs">{d.requested_by_name || "-"}</TableCell>
                    <TableCell className="text-xs">{fmtTs(d.requested_at)}</TableCell>
                    <TableCell className="text-right space-x-2 whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => openPdf(api, "production-cancellation", d.id, d.doc_no)} data-testid={`bp-pdf-${d.doc_no}`}><FileText className="w-4 h-4" /></Button>
                      {canDecide && d.status === "DIAJUKAN" && (
                        <>
                          <Button size="sm" onClick={() => openDecision(d, "approve")} data-testid={`bp-approve-${d.doc_no}`}><Check className="w-4 h-4 mr-1" />Setujui</Button>
                          <Button size="sm" variant="destructive" onClick={() => openDecision(d, "reject")} data-testid={`bp-reject-${d.doc_no}`}><Ban className="w-4 h-4 mr-1" />Tolak</Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* ============ DIALOG KEPUTUSAN ============ */}
      <Dialog open={!!decision} onOpenChange={(o) => { if (!o) setDecision(null); }}>
        <DialogContent data-testid="bp-decision-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {decision?.mode === "approve" ? <ShieldAlert className="w-5 h-5 text-destructive" /> : <Ban className="w-5 h-5" />}
              {decision?.mode === "approve" ? "Setujui Pembatalan Produksi" : "Tolak Ajuan"}
            </DialogTitle>
          </DialogHeader>
          {decision && (
            <div className="space-y-4 text-sm">
              <div className="rounded-lg bg-muted p-3 space-y-1">
                <div><span className="text-muted-foreground">Work Order:</span> <b>{decision.doc.wo?.wo_no_offline || decision.doc.wo?.wo_no}</b></div>
                <div><span className="text-muted-foreground">Diajukan oleh:</span> {decision.doc.requested_by_name || "-"}</div>
                <div><span className="text-muted-foreground">Alasan:</span> {decision.doc.reason}</div>
              </div>
              {decision.mode === "approve" ? (
                <>
                  <div className="flex gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3">
                    <AlertTriangle className="w-5 h-5 text-destructive shrink-0" />
                    <p data-testid="bp-approve-warning">
                      Anda akan membatalkan <b>{decision.doc.proposed_count} unit</b> secara <b>PERMANEN</b>.
                      Nomor QR unit-unit itu akan <b>mati selamanya</b> dan tidak akan dipakai ulang.
                      Setelah disetujui, dokumen ini <b>tidak bisa dibatalkan</b> — bila keliru, jalan keluarnya adalah membuat Work Order baru.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox id="bp-ack" checked={ack} onCheckedChange={(v) => setAck(!!v)} data-testid="bp-ack" />
                    <Label htmlFor="bp-ack" className="cursor-pointer">Saya paham tindakan ini permanen dan menyetujui pembatalan {decision.doc.proposed_count} unit.</Label>
                  </div>
                </>
              ) : (
                <div className="space-y-2">
                  <Label>Alasan penolakan (opsional)</Label>
                  <Textarea value={decisionReason} onChange={(e) => setDecisionReason(e.target.value)} rows={2} data-testid="bp-reject-reason" />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecision(null)} disabled={busy}>Batal</Button>
            <Button variant={decision?.mode === "approve" ? "destructive" : "default"} onClick={confirmDecision}
                    disabled={busy || (decision?.mode === "approve" && !ack)} data-testid="bp-confirm-decision">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {decision?.mode === "approve" ? "Setujui & Batalkan Permanen" : "Tolak Ajuan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
